import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Single source of truth for the signed-in user.
 *
 * /auth/me is fetched exactly ONCE per app load, here. Everything that needs
 * the session (ProtectedRoute, AppShell's nav variant, Dashboard's greeting
 * and admin flag) reads from this context instead of re-fetching — previously
 * one Dashboard visit made up to four sequential /auth/me calls.
 *
 * Login/Signup call setUser() with the response they already have, so no
 * verification round trip is needed after authenticating.
 */
const AuthContext = createContext(null);

/**
 * The session lives ONLY in an httpOnly cookie, which JavaScript cannot read —
 * so there is no token for the app to attach and nothing for an XSS payload to
 * steal. Every request just needs `credentials: 'include'`.
 *
 * Kept as a no-op returning an empty object so call sites can keep spreading
 * `...authHeaders()` without each one growing a special case.
 */
export function authHeaders() {
  return {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (res.ok) {
        const me = await res.json();
        setUser(me);
        return me;
      }
      setUser(null);
      return null;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, setUser, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * What `useAuth` resolves to with no AuthProvider above it.
 *
 * Every call site destructures the result, so returning the raw `null` default
 * meant any component rendered outside the provider crashed on its first line
 * rather than degrading. Signed-out is the safe reading: `loading: false` with
 * no user resolves AppShell's 'auto' variant to 'public' and sends
 * ProtectedRoute to /login, so nothing gated can render by accident.
 *
 * The real app always has the provider (App.js wraps the router in it). This
 * matters for tests that render a single page in isolation — and it became
 * load-bearing once the signed-out pages started going through AppShell, which
 * reads the session to decide between website chrome and app chrome.
 */
const NO_SESSION = Object.freeze({
  user: null,
  loading: false,
  setUser: () => {},
  refresh: async () => null,
});

export function useAuth() {
  return useContext(AuthContext) ?? NO_SESSION;
}
